import { useContext, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Grid,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DoneAll,
  ExpandMore,
  InfoOutlined,
  RemoveDone,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { RelatoriosContext } from 'contexts/RelatoriosContext';
import { ProdutosContext } from 'contexts/ProdutosContext';
import { CustomPagination } from 'utils/functions';
import Loader from 'components/Loader';
import Card from 'components/Card';
import Blank from 'assets/blank.png';
import localeText from 'utils/localeText';
import styles from './styles';
import moment from 'moment';

const ProdutoModal = ({ item }) => {
  const [tab, setTab] = useState(0);
  const [produto, setProduto] = useState(null);
  const { getURLRelatorio } = useContext(RelatoriosContext);
  const { getProdutoIndicadores, getLoading } = useContext(ProdutosContext);
  const estoque = produto?.indicadores?.estoque || [];
  const resumo = produto?.indicadores?.formacaoCusto?.resumo || {};
  const reposicao = produto?.indicadores?.formacaoCusto?.reposicao || {};
  const venda = produto?.indicadores?.formacaoCusto?.venda || {};
  const is_produto_fabricado = Boolean(reposicao?.dados?.ordem_fabricacao);

  const columnLeft = [
    { label: 'Referência', value: produto?.referencia },
    { label: 'Altura', value: produto?.altura },
    { label: 'Largura', value: produto?.largura },
    { label: 'Comprimento', value: produto?.comprimento },
    { label: 'Peso', value: produto?.peso },
    { label: 'Volume', value: produto?.volume },
  ];

  const columnRight = [
    { label: 'Código', value: produto?.codigo },
    { label: 'Grupo', value: produto?.grupo },
    { label: 'Linha', value: produto?.linha },
    { label: 'Marca', value: produto?.marca },
    { label: 'Modelo', value: produto?.modelo },
    { label: 'Localizador', value: produto?.localizador },
    { label: 'Unidade de Medida', value: produto?.unidade_medida },
  ];

  useEffect(() => {
    getProdutoIndicadores({
      id: item?.produto_id,
      item_id: item?.id,
      cb: setProduto,
    });
  }, []);

  if (getLoading) return <Loader />;

  const Column = ({ rows, size }) => (
    <Box flex={size} display="flex" flexDirection="column">
      {rows.map((r, i) => (
        <Typography key={i} variant="caption" fontWeight="bold">
          {r.label}: <Typography variant="caption">{r.value || '-'}</Typography>
        </Typography>
      ))}
    </Box>
  );

  const Line = ({ label, value }) =>
    value ? (
      <Typography variant="caption" fontWeight="bold">
        {label}: <Typography variant="caption">{value}</Typography>
      </Typography>
    ) : null;

  const CardResumo = ({ label, value, percentual }) => (
    <Card style={styles?.card}>
      <Box display="flex" gap={2}>
        <Typography fontWeight="bold" flex={1}>
          {label}
        </Typography>
        {percentual > 0 && (
          <Typography variant="body2" fontWeight="bold" color="text.secondary">
            {percentual?.toLocaleString('pt-br', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            })}
            %
          </Typography>
        )}
      </Box>
      <Typography variant="h6" fontWeight="bold" color="primary">
        {value?.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}
        {item?.quantidade > 0 && (
          <Typography variant="caption" color="text.secondary" ml={1}>
            x {item?.quantidade} {item?.UnidadeMedida?.sigla || ''} ={' '}
            {(value * item?.quantidade)?.toLocaleString('pt-br', {
              style: 'currency',
              currency: 'BRL',
            })}
          </Typography>
        )}
      </Typography>
    </Card>
  );

  const DadosReposicao = ({ dados }) => {
    const arr = [
      { label: 'Documento', value: dados?.documento },
      {
        label: 'Data de Entrada',
        value:
          dados?.dtentrada && moment(dados?.dtentrada).format('DD/MM/YYYY'),
      },
      { label: 'Fornecedor', value: dados?.fornecedor },
      { label: 'Usuário', value: dados?.usuario },
      { label: 'Moeda', value: dados?.moeda },
      {
        label: 'Cotação da Moeda',
        value:
          dados?.moeda_cotacao &&
          dados?.moeda_cotacao.toLocaleString('pt-br', {
            style: 'currency',
            currency: 'BRL',
          }),
      },
      {
        label: 'Cotação da Moeda Atual',
        value:
          dados?.moeda_cotacao_atual &&
          dados?.moeda_cotacao_atual.toLocaleString('pt-br', {
            style: 'currency',
            currency: 'BRL',
          }),
      },
      { label: 'Ordem de Fabricação', value: dados?.ordem_fabricacao },
      { label: 'Unidade de Medida', value: dados?.unidade_medida },
      {
        label: 'Quantidade Fornecida',
        value: `${dados?.quantidade_fornecida || 0} ${dados?.unidade_medida || ''}`,
      },
      {
        label: 'Quantidade em Estoque',
        value: `${dados?.quantidade_estoque || 0} ${dados?.unidade_medida || ''}`,
      },
    ];
    const expanded = arr?.length > 0 ? undefined : false;
    const hideIcon = arr?.length > 0 ? {} : styles?.hideIcon;

    return (
      <Accordion expanded={expanded}>
        <AccordionSummary expandIcon={<ExpandMore sx={hideIcon} />}>
          <Typography variant="body1" fontWeight="bold">
            Dados
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {arr?.map(({ label, value }, i) => (
            <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
              <Typography variant="caption" fontWeight="bold" flex={2}>
                {label}:
              </Typography>
              <Typography variant="caption" flex={3}>
                {value || '-'}
              </Typography>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>
    );
  };

  const DadosVenda = ({ dados }) => {
    const arr = [
      { label: 'Documento', value: dados?.documento },
      {
        label: 'Data de Emissao',
        value:
          dados?.dtemissao && moment(dados?.dtemissao).format('DD/MM/YYYY'),
      },
      { label: 'Cliente', value: dados?.cliente },
      { label: 'Usuário', value: dados?.usuario },
    ];
    const expanded = arr?.length > 0 ? undefined : false;
    const hideIcon = arr?.length > 0 ? {} : styles?.hideIcon;

    return (
      <Accordion expanded={expanded}>
        <AccordionSummary expandIcon={<ExpandMore sx={hideIcon} />}>
          <Typography variant="body1" fontWeight="bold">
            Dados
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {arr?.map(({ label, value }, i) => (
            <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
              <Typography variant="caption" fontWeight="bold" flex={2}>
                {label}:
              </Typography>
              <Typography variant="caption" flex={3}>
                {value || '-'}
              </Typography>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>
    );
  };

  const Detalhes = ({ detalhes = {}, total = 0 }) => {
    const arr = Object.keys(detalhes)?.map((k) => ({
      label: k,
      value: detalhes[k] || 0,
    }));
    const expanded = arr?.length > 0 ? undefined : false;
    const hideIcon = arr?.length > 0 ? {} : styles?.hideIcon;

    return (
      <Accordion expanded={expanded}>
        <AccordionSummary expandIcon={<ExpandMore sx={hideIcon} />}>
          <Box
            mr={1}
            flex={1}
            display="flex"
            alignItems="center"
            flexDirection="row"
            justifyContent="space-between"
          >
            <Typography variant="body1" fontWeight="bold">
              Detalhes
            </Typography>
            <Typography
              variant="body1"
              fontWeight="bold"
              color="text.secondary"
            >
              {total?.toLocaleString('pt-br', {
                style: 'currency',
                currency: 'BRL',
              })}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {arr?.map(({ label, value }, i) => (
            <Box key={i} gap={0.5} display="flex" alignItems="flex-end">
              <Typography variant="caption">{label}</Typography>
              <Box flex={1} borderBottom="1px dotted" mb={0.5} />
              <Typography variant="caption">
                {value?.toLocaleString('pt-br', {
                  style: 'currency',
                  currency: 'BRL',
                })}
              </Typography>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>
    );
  };

  const Tributos = ({ tributos = [], total = 0 }) => {
    const expanded = tributos?.length > 0 ? undefined : false;
    const hideIcon = tributos?.length > 0 ? {} : styles?.hideIcon;

    return (
      <Accordion expanded={expanded}>
        <AccordionSummary expandIcon={<ExpandMore sx={hideIcon} />}>
          <Box
            mr={1}
            flex={1}
            display="flex"
            alignItems="center"
            flexDirection="row"
            justifyContent="space-between"
          >
            <Typography variant="body1" fontWeight="bold">
              Tributos
            </Typography>
            <Typography
              variant="body1"
              fontWeight="bold"
              color="text.secondary"
            >
              {total?.toLocaleString('pt-br', {
                style: 'currency',
                currency: 'BRL',
              })}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {tributos?.map((t, i) => (
            <Box key={i} gap={0.5} display="flex" alignItems="flex-end">
              <Typography variant="caption">{t?.tributo}</Typography>
              <Box flex={1} borderBottom="1px dotted" mb={0.5} />
              <Typography variant="caption">
                {(t?.valor || 0)?.toLocaleString('pt-br', {
                  style: 'currency',
                  currency: 'BRL',
                })}
              </Typography>
              <Typography variant="caption">
                (
                {(t?.percentual || 0)?.toLocaleString('pt-br', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                })}
                %)
              </Typography>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>
    );
  };

  const Materiais = ({ materiais = [], total = 0 }) => {
    const expanded = materiais?.length > 0 ? undefined : false;
    const hideIcon = materiais?.length > 0 ? {} : styles?.hideIcon;

    return (
      <Accordion expanded={expanded}>
        <AccordionSummary expandIcon={<ExpandMore sx={hideIcon} />}>
          <Box
            mr={1}
            flex={1}
            display="flex"
            alignItems="center"
            flexDirection="row"
            justifyContent="space-between"
          >
            <Typography variant="body1" fontWeight="bold">
              Materiais
            </Typography>
            <Typography
              variant="body1"
              fontWeight="bold"
              color="text.secondary"
            >
              {total?.toLocaleString('pt-br', {
                style: 'currency',
                currency: 'BRL',
              })}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <DataGrid
            rows={materiais}
            columns={[
              {
                field: 'material',
                headerName: 'Material',
                minWidth: 250,
                flex: 1,
                sortable: false,
              },
              {
                field: 'quantidade',
                headerName: 'Quantidade',
                width: 100,
                sortable: false,
                type: 'number',
              },
              {
                field: 'perda',
                headerName: 'Perda',
                width: 100,
                sortable: false,
                type: 'number',
              },
              {
                field: 'custo_unitario',
                headerName: 'Custo Unitário',
                width: 150,
                sortable: false,
                type: 'number',
                valueGetter: ({ value }) =>
                  (value || 0)?.toLocaleString('pt-br', {
                    style: 'currency',
                    currency: 'BRL',
                  }),
              },
              {
                field: 'custo_total',
                headerName: 'Custo Total',
                width: 150,
                sortable: false,
                type: 'number',
                valueGetter: ({ value }) =>
                  (value || 0)?.toLocaleString('pt-br', {
                    style: 'currency',
                    currency: 'BRL',
                  }),
              },
              {
                field: 'data_compra',
                headerName: 'Data da Compra',
                width: 150,
                sortable: false,
                valueGetter: ({ value }) =>
                  value && moment(value).format('DD/MM/YYYY'),
              },
            ]}
            autoHeight
            pagination
            pageSize={10}
            density="compact"
            disableColumnMenu
            showCellRightBorder
            showColumnRightBorder
            disableSelectionOnClick
            localeText={localeText}
            getRowHeight={() => 'auto'}
            components={{ Pagination: CustomPagination }}
          />
        </AccordionDetails>
      </Accordion>
    );
  };

  const Processos = ({ processos = [], total = 0 }) => {
    const expanded = processos?.length > 0 ? undefined : false;
    const hideIcon = processos?.length > 0 ? {} : styles?.hideIcon;

    return (
      <Accordion expanded={expanded}>
        <AccordionSummary expandIcon={<ExpandMore sx={hideIcon} />}>
          <Box
            mr={1}
            flex={1}
            display="flex"
            alignItems="center"
            flexDirection="row"
            justifyContent="space-between"
          >
            <Typography variant="body1" fontWeight="bold">
              Processos
            </Typography>
            <Typography
              variant="body1"
              fontWeight="bold"
              color="text.secondary"
            >
              {total?.toLocaleString('pt-br', {
                style: 'currency',
                currency: 'BRL',
              })}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <DataGrid
            rows={processos}
            columns={[
              {
                field: 'descricao',
                headerName: 'Descrição',
                minWidth: 250,
                flex: 1,
                sortable: false,
              },
              {
                field: 'operacao',
                headerName: 'Operação',
                width: 100,
                sortable: false,
                type: 'number',
              },
              {
                field: 'ccusto',
                headerName: 'Centro de Custo',
                minWidth: 250,
                flex: 1,
                sortable: false,
              },
              {
                field: 'tempo_previsto',
                headerName: 'Tempo Previsto',
                width: 150,
                sortable: false,
                type: 'number',
                valueGetter: ({ value }) => {
                  const duration = moment.duration(value, 'minutes');
                  const horas = Math.floor(duration.asHours());
                  const mins = duration.minutes();
                  const secs = duration.seconds();

                  return `${horas}h: ${mins}m: ${secs}s`;
                },
              },
              {
                field: 'tempo_apontado',
                headerName: 'Tempo Apontado',
                width: 150,
                sortable: false,
                type: 'number',
                valueGetter: ({ value }) => {
                  const duration = moment.duration(value, 'minutes');
                  const horas = Math.floor(duration.asHours());
                  const mins = duration.minutes();
                  const secs = duration.seconds();

                  return `${horas}h: ${mins}m: ${secs}s`;
                },
              },
              {
                field: 'valor',
                headerName: 'Valor Total',
                width: 150,
                sortable: false,
                type: 'number',
                valueGetter: ({ value }) =>
                  (value || 0)?.toLocaleString('pt-br', {
                    style: 'currency',
                    currency: 'BRL',
                  }),
              },
            ]}
            autoHeight
            pagination
            pageSize={10}
            density="compact"
            disableColumnMenu
            showCellRightBorder
            showColumnRightBorder
            disableSelectionOnClick
            localeText={localeText}
            getRowHeight={() => 'auto'}
            components={{ Pagination: CustomPagination }}
          />
        </AccordionDetails>
      </Accordion>
    );
  };

  const MaoDeObra = ({ dados, total = 0 }) => {
    const arr = [
      { label: 'Valor', value: dados?.valor_mao_de_obra },
      { label: 'Receita', value: dados?.receita },
      { label: 'Percentual', value: dados?.mao_de_obra, percentual: true },
    ];
    const expanded = total > 0 ? undefined : false;
    const hideIcon = total > 0 ? {} : styles?.hideIcon;

    return (
      <Accordion expanded={expanded}>
        <AccordionSummary expandIcon={<ExpandMore sx={hideIcon} />}>
          <Box
            mr={1}
            flex={1}
            display="flex"
            alignItems="center"
            flexDirection="row"
            justifyContent="space-between"
          >
            <Typography variant="body1" fontWeight="bold">
              Mão de Obra Indireta
            </Typography>
            <Typography
              variant="body1"
              fontWeight="bold"
              color="text.secondary"
            >
              {total?.toLocaleString('pt-br', {
                style: 'currency',
                currency: 'BRL',
              })}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {arr?.map(({ label, value, percentual }, i) => (
            <Box key={i} gap={0.5} display="flex" alignItems="flex-end">
              <Typography variant="caption">{label}</Typography>
              <Box flex={1} borderBottom="1px dotted" mb={0.5} />
              <Typography variant="caption">
                {(value || 0)?.toLocaleString('pt-br', {
                  ...(percentual
                    ? { minimumFractionDigits: 0, maximumFractionDigits: 2 }
                    : { style: 'currency', currency: 'BRL' }),
                })}
              </Typography>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>
    );
  };

  const GastosGerais = ({ dados, total = 0 }) => {
    const arr = [
      { label: 'Valor', value: dados?.valor_ggf },
      { label: 'Percentual', value: dados?.ggf, percentual: true },
    ];
    const expanded = total > 0 ? undefined : false;
    const hideIcon = total > 0 ? {} : styles?.hideIcon;

    return (
      <Accordion expanded={expanded}>
        <AccordionSummary expandIcon={<ExpandMore sx={hideIcon} />}>
          <Box
            mr={1}
            flex={1}
            display="flex"
            alignItems="center"
            flexDirection="row"
            justifyContent="space-between"
          >
            <Typography variant="body1" fontWeight="bold">
              Gastos Gerais de Fabricação
            </Typography>
            <Typography
              variant="body1"
              fontWeight="bold"
              color="text.secondary"
            >
              {total?.toLocaleString('pt-br', {
                style: 'currency',
                currency: 'BRL',
              })}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {arr?.map(({ label, value, percentual }, i) => (
            <Box key={i} gap={0.5} display="flex" alignItems="flex-end">
              <Typography variant="caption">{label}</Typography>
              <Box flex={1} borderBottom="1px dotted" mb={0.5} />
              <Typography variant="caption">
                {(value || 0)?.toLocaleString('pt-br', {
                  ...(percentual
                    ? { minimumFractionDigits: 0, maximumFractionDigits: 2 }
                    : { style: 'currency', currency: 'BRL' }),
                })}
              </Typography>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} mx={2}>
        <Box display="flex" gap={2}>
          <Box flex={1} position="relative">
            <Chip
              size="small"
              sx={styles?.chip}
              label={produto?.ativo === 'SIM' ? 'ATIVO' : 'INATIVO'}
              color={produto?.ativo === 'SIM' ? 'primary' : 'error'}
              icon={produto?.ativo === 'SIM' ? <DoneAll /> : <RemoveDone />}
            />
            <Box
              alt="Imagem"
              component="img"
              sx={styles?.img}
              src={produto?.url || Blank}
            />
          </Box>
          <Column size={1} rows={columnLeft} />
          <Column size={2} rows={columnRight} />
        </Box>
      </Grid>
      <Grid item xs={12} mx={2} display="flex" flexDirection="column">
        <Line label="NCM" value={produto?.ncm} />
        <Line label="Origem Tributária" value={produto?.origem_tributaria} />
        <Line label="Complemento 1" value={produto?.complemento1} />
        <Line label="Complemento 2" value={produto?.complemento2} />
      </Grid>
      <Grid item xs={12}>
        <Tabs variant="fullWidth" value={tab} onChange={(_, v) => setTab(v)}>
          <Tab value={0} label="Saldo" />
          <Tab value={1} label="Reposição" />
          <Tab value={2} label="Venda" />
        </Tabs>
      </Grid>
      {[1, 2].includes(tab) && (
        <Grid item xs={12} mx={2}>
          <Grid container spacing={2}>
            <Grid item xs={6} display="flex" flexDirection="column">
              <CardResumo label="Reposição" value={resumo?.valor_reposicao} />
            </Grid>
            <Grid item xs={6} display="flex" flexDirection="column">
              <CardResumo label="Venda Líquida" value={resumo?.valor_venda} />
            </Grid>
            <Grid item xs={6} display="flex" flexDirection="column">
              <CardResumo
                label="Despesas Fixas"
                value={resumo?.valor_despesas_fixas}
                percentual={resumo?.percentual_despesas_fixas}
              />
            </Grid>
            <Grid item xs={6} display="flex" flexDirection="column">
              <CardResumo
                label="Despesas Variáveis"
                value={resumo?.valor_despesas_variaveis}
                percentual={resumo?.percentual_despesas_variaveis}
              />
            </Grid>
            <Grid item xs={6} display="flex" flexDirection="column">
              <CardResumo
                label="Margem de Contribuição"
                value={resumo?.valor_margem}
                percentual={resumo?.percentual_margem}
              />
            </Grid>
            <Grid item xs={6} display="flex" flexDirection="column">
              <CardResumo
                label="Margem de Lucro"
                value={resumo?.valor_lucro}
                percentual={resumo?.percentual_lucro}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
      {tab === 0 && (
        <Grid item xs={12} mx={2}>
          {estoque?.map((p, i) => {
            const rows = [
              ...p?.locais,
              { disponibilizar: 'NAO', local: 'Reserva', saldo: p?.reserva },
            ];
            return (
              <Box key={i} mb={2}>
                <Typography variant="body1" fontWeight="bold" mb={1}>
                  {p?.filial}
                </Typography>
                <DataGrid
                  rows={rows}
                  columns={[
                    {
                      field: 'local',
                      headerName: 'Local',
                      minWidth: 150,
                      flex: 1,
                      sortable: false,
                    },
                    {
                      field: 'disponibilizar',
                      headerName: 'Disponível',
                      width: 100,
                      sortable: false,
                      type: 'boolean',
                      valueGetter: ({ value }) => value === 'SIM',
                    },
                    {
                      field: 'saldo',
                      headerName: 'Saldo',
                      width: 150,
                      sortable: false,
                      renderCell: ({ row }) => (
                        <Box
                          gap={2}
                          flex={1}
                          display="flex"
                          alignItems="center"
                        >
                          <Typography variant="body2" flex={1}>
                            {row?.saldo || 0} {produto?.unidade_medida}
                          </Typography>
                          <Tooltip title="Relatório">
                            <IconButton
                              size="small"
                              onClick={() =>
                                getURLRelatorio({
                                  data: {
                                    codigo:
                                      row?.local === 'Reserva'
                                        ? 'RES0000001'
                                        : 'MVE0000001',
                                    prd: item?.produto_id,
                                    filial: p?.filial_id,
                                    local: row?.local_id,
                                  },
                                })
                              }
                            >
                              <InfoOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ),
                    },
                  ]}
                  autoHeight
                  hideFooter
                  density="compact"
                  getRowId={(row) => JSON.stringify(row)}
                  disableSelectionOnClick
                  disableColumnMenu
                  showCellRightBorder
                  showColumnRightBorder
                  localeText={localeText}
                />
              </Box>
            );
          })}
        </Grid>
      )}
      {tab === 1 && (
        <Grid item xs={12} mx={2}>
          <DadosReposicao dados={reposicao?.dados} />
          {!is_produto_fabricado && (
            <Detalhes
              detalhes={reposicao?.detalhes}
              total={reposicao?.total_detalhes}
            />
          )}
          {!is_produto_fabricado && (
            <Tributos
              tributos={reposicao?.tributos}
              total={reposicao?.total_tributos}
            />
          )}
          {is_produto_fabricado && (
            <Materiais
              materiais={reposicao?.materiais}
              total={reposicao?.total_materiais}
            />
          )}
          {is_produto_fabricado && (
            <Processos
              processos={reposicao?.processos}
              total={reposicao?.total_processos}
            />
          )}
          <MaoDeObra
            dados={reposicao?.dados}
            total={reposicao?.total_mao_de_obra}
          />
          {is_produto_fabricado && (
            <GastosGerais
              dados={reposicao?.dados}
              total={reposicao?.total_ggf}
            />
          )}
        </Grid>
      )}
      {tab === 2 && (
        <Grid item xs={12} mx={2}>
          <DadosVenda dados={venda?.dados} />
          <Detalhes detalhes={venda?.detalhes} total={venda?.total_detalhes} />
          <Tributos tributos={venda?.tributos} total={venda?.total_tributos} />
        </Grid>
      )}
    </Grid>
  );
};

export default ProdutoModal;
